import { useRef, useState } from "react";
import { toast } from "sonner";
import { FileText, ImageIcon, Paperclip, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import * as purchaseApi from "@/lib/purchaseApi";

/**
 * The bill / invoice files on a purchase or a return (owner, 2026-11-01: "add media
 * upload option per purchase record so that we can upload the bill/invoice").
 *
 * A photo is redrawn here at most BILL_EDGE pixels on its long side — big enough to
 * read a supplier's small print, small enough to pass the server's request limit. A
 * PDF goes up as it is, so its size is checked here first and refused in words.
 */
const BILL_EDGE = 2000;
const JPEG_QUALITY = 0.85;
export const MAX_FILE_BYTES = 4 * 1024 * 1024;
const ACCEPT = "image/*,application/pdf";

const errorText = failure => {
  const detail = failure?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (failure?.response?.status === 413) return "The file is too large — the limit is 4 MB";
  return failure?.message || "Could not reach the server";
};

/** A photo as a readable JPEG, or the file itself (PDF, or a browser that cannot redraw it). */
export async function prepareBillFile(file) {
  if (!String(file.type || "").startsWith("image/") || typeof createImageBitmap !== "function") return file;
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, BILL_EDGE / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    const ctx = canvas.getContext("2d");
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise(resolve => canvas.toBlob(resolve, "image/jpeg", JPEG_QUALITY));
    if (!blob) return file;
    const name = String(file.name || "bill").replace(/\.[^.]+$/, "") + ".jpg";
    return new File([blob], name, { type: "image/jpeg" });
  } catch {
    return file;
  }
}

/** Upload files one by one; returns how many went up. Each failure is named. */
export async function uploadBillFiles(files, upload) {
  let done = 0;
  for (const original of files) {
    const file = await prepareBillFile(original);
    if (file.size > MAX_FILE_BYTES) {
      toast.error(`${original.name}: ${(file.size / 1024 / 1024).toFixed(1)} MB — the limit is 4 MB`);
      continue;
    }
    const result = await upload(file);
    if (result.ok) done += 1;
    else toast.error(`${original.name} not attached: ${result.error}`);
  }
  return done;
}

const sizeText = bytes => (bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.ceil(bytes / 1024)} KB`);

export default function BillFiles({ files = [], parent, token, canEdit, onUpload, onDelete, testId = "bill-files",
                                    fetchFile = purchaseApi.fetchAttachment, title = "Bill / invoice",
                                    emptyText = "No bill attached yet.", addedText = "Bill attached" }) {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);

  const pick = async event => {
    const chosen = Array.from(event.target.files || []);
    event.target.value = "";
    if (!chosen.length) return;
    setBusy(true);
    const done = await uploadBillFiles(chosen, file => onUpload(parent, file));
    setBusy(false);
    if (done) toast.success(done === 1 ? addedText : `${done} files attached`);
  };

  // The file needs the login on its request, so it is fetched and opened as a blob.
  const open = async file => {
    try {
      const blob = await fetchFile(file.id, token);
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (failure) {
      toast.error(`Could not open ${file.file_name}: ${errorText(failure)}`);
    }
  };

  const remove = async file => {
    if (!window.confirm(`Delete ${file.file_name} from this record?`)) return;
    const result = await onDelete(file.id);
    if (result.ok) toast.success(`${file.file_name} deleted`);
    else toast.error(`Not deleted: ${result.error}`);
  };

  return (
    <section className="space-y-snug" data-testid={testId}>
      <div className="flex items-center justify-between gap-snug">
        <h3 className="text-body font-semibold flex items-center gap-snug"><Paperclip className="w-4 h-4" />{title}</h3>
        {canEdit && (
          <>
            {/* No `capture`: it would open only the camera; without it a phone offers
                camera, gallery and files (a PDF the supplier sent on WhatsApp). */}
            <input ref={input} type="file" accept={ACCEPT} multiple className="hidden"
                   onChange={pick} data-testid={`${testId}-input`} />
            <Button variant="outline" size="sm" disabled={busy} onClick={() => input.current?.click()}
                    data-testid={`${testId}-add`}>
              {busy ? "Uploading…" : "Add photo or PDF"}
            </Button>
          </>
        )}
      </div>
      {files.length === 0 && (
        <p className="text-meta text-muted-foreground" data-testid={`${testId}-none`}>{emptyText}</p>
      )}
      {files.map(file => (
        <div key={file.id} className="flex items-center gap-snug rounded-md border bg-card px-snug py-hair"
             data-testid={`${testId}-file-${file.id}`}>
          {file.content_type === "application/pdf" ? <FileText className="w-4 h-4 shrink-0" /> : <ImageIcon className="w-4 h-4 shrink-0" />}
          <button type="button" className="flex-1 min-w-0 text-left text-body text-primary underline-offset-2 hover:underline truncate"
                  onClick={() => open(file)} data-testid={`${testId}-open-${file.id}`}>
            {file.file_name}
          </button>
          <span className="text-micro text-muted-foreground tabular-nums shrink-0">{sizeText(file.size_bytes)}</span>
          {canEdit && (
            <Button variant="ghost" size="icon" aria-label={`Delete ${file.file_name}`} onClick={() => remove(file)}
                    data-testid={`${testId}-delete-${file.id}`}>
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      ))}
    </section>
  );
}
